/** Order Workspace building blocks — Next Action card · Visual Workflow stepper + stage drawer. */
import { useNavigate } from "react-router-dom";
import { Check, Circle, ArrowRight, Clock, User, FileText, AlertTriangle, StickyNote, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { StatusPill } from "@/components/os/OsPrimitives";

export function NextActionCard({ action, onGo }) {
  if (!action) return <div className="os-skeleton h-24" />;
  const done = !!action.done;
  return (
    <section className={"os-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden " + (done ? "" : "border-[color:var(--st-accent)]/40")} data-testid="order-next-action">
      {!done && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[color:var(--st-accent)]" />}
      <div className="flex-1 min-w-0 pl-1">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 text-[color:var(--st-accent)]">
          <Sparkles className="h-3.5 w-3.5" /> Next action
        </div>
        <div className="font-display font-bold text-lg sm:text-xl tracking-tight text-[color:var(--st-text)] mt-1" data-testid="order-next-action-label">{action.label}</div>
        {action.why && <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">{action.why}</div>}
      </div>
      {!done && (
        <button onClick={() => onGo(action)} data-testid="order-next-action-btn"
          className="h-10 px-5 rounded-full bg-[color:var(--st-accent)] text-white text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.98] transition-[filter,transform] shrink-0 shadow-[0_6px_16px_rgba(36,107,253,0.25)]">
          {action.label.replace(/ ·.*$/, "")} <ArrowRight className="h-4 w-4" />
        </button>
      )}
      {done && <StatusPill tone="good">Complete</StatusPill>}
    </section>
  );
}

const NODE = {
  done: "bg-[color:var(--st-success)] text-white border-[color:var(--st-success)]",
  active: "bg-white text-[color:var(--st-accent)] border-[color:var(--st-accent)] ring-4 ring-[color:var(--st-accent)]/15",
  pending: "bg-white text-[color:var(--st-text-hint)] border-[color:var(--st-border-strong)]",
  skipped: "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-subtle)] border-[color:var(--st-border)]",
};

export function WorkflowStepper({ stages, onSelect, selectedKey }) {
  if (!stages) return <div className="os-skeleton h-20" />;
  return (
    <section className="os-card px-4 sm:px-6 py-5 overflow-x-auto min-w-0 max-w-full" data-testid="order-workflow">
      <ol className="flex items-start min-w-[640px]">
        {stages.map((s, i) => {
          const last = i === stages.length - 1;
          const sel = selectedKey === s.key;
          return (
            <li key={s.key} className={"flex-1 flex items-start " + (last ? "flex-none" : "")} data-testid={`order-stage-${s.key}`}>
              <button onClick={() => onSelect(s)} data-testid={`order-stage-${s.key}-btn`} aria-pressed={sel}
                className="group flex flex-col items-center w-[96px] text-center focus:outline-none">
                <span className={"h-9 w-9 rounded-full border-2 grid place-items-center transition-[transform,box-shadow] group-hover:scale-105 " + NODE[s.state] + (sel ? " shadow-[0_0_0_6px_rgba(36,107,253,0.12)]" : "")}>
                  {s.state === "done" ? <Check className="h-4 w-4" strokeWidth={3} /> : s.state === "active" ? <span className="h-2.5 w-2.5 rounded-full bg-[color:var(--st-accent)] animate-pulse" /> : <Circle className="h-3 w-3" />}
                </span>
                <span className={"mt-2 text-[10.5px] uppercase tracking-[0.12em] font-semibold " + (s.state === "pending" || s.state === "skipped" ? "text-[color:var(--st-text-subtle)]" : "text-[color:var(--st-text)]")}>{s.label}</span>
                {s.status && <span className="mt-0.5 text-[10.5px] text-[color:var(--st-text-muted)] truncate max-w-[96px]">{s.status}</span>}
              </button>
              {!last && <span className={"flex-1 h-[2px] mt-[17px] -mx-6 rounded " + (s.state === "done" ? "bg-[color:var(--st-success)]" : "bg-[color:var(--st-border)]")} />}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const SLA_TONE = { on_track: "good", at_risk: "warn", breached: "critical" };
const STATE_TONE = { done: "good", active: "info", pending: "neutral", skipped: "neutral" };

export function StageDrawer({ stage, onClose, onOpenDoc }) {
  return (
    <Sheet open={!!stage} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="order-stage-drawer">
        {stage && (
          <>
            <SheetHeader className="text-left">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)] font-semibold">Workflow stage</div>
              <SheetTitle className="font-display text-xl flex items-center gap-2">{stage.label} <StatusPill tone={STATE_TONE[stage.state]}>{stage.state}</StatusPill></SheetTitle>
            </SheetHeader>
            <dl className="mt-5 grid grid-cols-2 gap-3 text-[13px]">
              <Fact icon={User} label="Owner" value={stage.owner} testId="stage-owner" />
              <Fact icon={Clock} label="Date" value={stage.date} testId="stage-date" />
              <Fact icon={FileText} label="Status" value={stage.status} testId="stage-status" wide />
              {stage.sla && (
                <div className="col-span-2 rounded-lg border border-[color:var(--st-border)] px-3 py-2.5" data-testid="stage-sla">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold mb-1">SLA · required delivery</div>
                  <div className="flex items-center gap-2"><span className="font-mono">{stage.sla.due}</span>
                    <StatusPill tone={SLA_TONE[stage.sla.state]}>{stage.sla.days_left < 0 ? `${-stage.sla.days_left}d late` : `${stage.sla.days_left}d left`}</StatusPill></div>
                </div>
              )}
            </dl>
            {stage.pending_reason && (
              <div className="mt-4 rounded-lg bg-[color:var(--st-warn-soft)] border border-[color:var(--st-warn)]/30 px-3 py-2.5 text-[12.5px] text-[color:var(--st-warn)] flex gap-2" data-testid="stage-pending-reason">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {stage.pending_reason}
              </div>
            )}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold mb-1.5 flex items-center gap-1"><StickyNote className="h-3.5 w-3.5" /> Notes</div>
              {stage.notes?.length ? <ul className="space-y-1 text-[12.5px] text-[color:var(--st-text)]" data-testid="stage-notes">{stage.notes.map((n, i) => <li key={i} className="rounded-md bg-[color:var(--st-surface-alt)] px-2.5 py-1.5">{n}</li>)}</ul>
                : <div className="text-[12px] text-[color:var(--st-text-subtle)]">No notes recorded for this stage.</div>}
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold mb-1.5 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Documents</div>
              {stage.documents?.length ? (
                <ul className="space-y-1" data-testid="stage-documents">
                  {stage.documents.map((d, i) => (
                    <li key={i}>
                      <button onClick={() => onOpenDoc(d)} className="w-full text-left rounded-md border border-[color:var(--st-border)] px-2.5 py-2 text-[12.5px] hover:bg-[color:var(--st-surface-alt)] flex items-center gap-2">
                        <StatusPill tone="neutral" dot={false}>{(d.kind || "").replace(/_/g, " ")}</StatusPill><span className="truncate">{d.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-[12px] text-[color:var(--st-text-subtle)]">No documents at this stage yet.</div>}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Fact({ icon: Icon, label, value, testId, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""} data-testid={testId}>
      <dt className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</dt>
      <dd className="mt-0.5 text-[color:var(--st-text)]">{value || <span className="text-[color:var(--st-text-subtle)]">—</span>}</dd>
    </div>
  );
}

export function WorkspaceTabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-[color:var(--st-border)]" role="tablist" data-testid="order-tabs">
      {tabs.map((t) => {
        const on = active === t.key;
        return (
          <button key={t.key} role="tab" aria-selected={on} onClick={() => onChange(t.key)} data-testid={`order-tab-${t.key}`}
            className={"relative h-10 px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors " + (on ? "text-[color:var(--st-text)]" : "text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")}>
            {t.label}{t.count != null && <span className="ml-1.5 text-[10.5px] text-[color:var(--st-text-subtle)]">{t.count}</span>}
            {on && <span className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full bg-[color:var(--st-accent)]" />}
          </button>
        );
      })}
    </div>
  );
}

export function useOpenDoc(API, orderId) {
  const navigate = useNavigate();
  return (d) => {
    if (d.url) { window.open(d.url, "_blank"); return; }
    if (d.kind === "DELIVERY_CHALLAN" && d.ref_id) { window.open(`${API}/delivery-challans/${d.ref_id}/pdf`, "_blank"); return; }
    if (d.kind === "SHIPPING_LABEL" && d.ref_id) { window.open(`${API}/delivery-challans/${d.ref_id}/pdf`, "_blank"); return; }
    if (d.kind === "VENDOR_PO" && d.ref_id) { navigate(`/commercial/crm/vendor-pos?open=${d.ref_id}`); return; }
    if (d.kind === "SHIPMENT") { navigate("/shipments"); return; }
    if (d.kind === "ORDER") { window.open(`${API}/one-orders/${orderId}/pdf/confirmation`, "_blank"); }
  };
}
